// modernPlatformCard.jsx
import React from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  IconButton,
  Avatar,
  Stack,
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import { alpha, useTheme } from '@mui/material/styles';

export default function ModernPlatformCard({ 
  handle, 
  views, 
  following, 
  icon, 
  link
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const platformColors = {
    instagram: '#E4405F',
    youtube: '#FF0000',
    tiktok: '#111111',
    twitter: '#111111',
    x: '#111111',
    twitch: '#9146FF',
    podcast: '#7C3AED',
    kick: '#16A34A',
  };

  const formatNumber = (num) => {
    if (!num || num === 'N/A') return null;
    if (typeof num === 'string') return num;
    
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  };

  const getPlatformColor = () => {
    const platformName = `${handle || ''} ${link || ''}`.toLowerCase();
    for (const [platform, color] of Object.entries(platformColors)) {
      if (platformName.includes(platform)) {
        return color;
      }
    }
    return isDarkMode ? '#D1D5DB' : '#6B7280';
  };

  const formattedFollowers = formatNumber(following);
  const formattedViews = views && views !== 'N/A' ? formatNumber(views) : 'N/A';
  const platformColor = getPlatformColor();

  // Don't render if no data
  if (!formattedFollowers && !formattedViews) return null;

  return (
    <Card 
      elevation={0}
      sx={{ 
        height: '100%',
        minHeight: 198,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        border: `1px solid ${isDarkMode ? '#2A2A2A' : '#E5E7EB'}`,
        backgroundColor: isDarkMode ? '#1A1A1A' : '#FFFFFF',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isDarkMode
            ? '0 10px 22px rgba(0,0,0,0.3)'
            : '0 10px 22px rgba(15,23,42,0.06)',
          borderColor: isDarkMode ? '#343434' : '#D1D5DB',
          '& .launch-button': {
            opacity: 1,
            transform: 'translateX(0)',
          }
        }
      }}
    >
      <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            <Avatar
              sx={{
                width: 34,
                height: 34,
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : alpha(platformColor, 0.08),
                color: platformColor,
                mr: 1.5,
              }}
            >
              {React.cloneElement(icon, { sx: { fontSize: 16 } })}
            </Avatar>
            
            <Typography 
              variant="subtitle1" 
              fontWeight={700}
              sx={{ 
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                lineHeight: 1.2,
                color: isDarkMode ? '#FFFFFF' : '#1A1A1A',
              }}
            >
              {handle || 'Creator'}
            </Typography>
          </Box>

          {link && (
            <IconButton
              className="launch-button"
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{
                opacity: 0,
                transform: 'translateX(10px)',
                transition: 'all 0.2s ease',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : '#F3F4F6',
                color: isDarkMode ? '#D1D5DB' : '#6B7280',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : '#E5E7EB',
                }
              }}
            >
              <LaunchIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Stack spacing={2} sx={{ mt: 'auto' }}>
          {formattedFollowers && (
            <Box>
              <Typography 
                variant="caption" 
                color="text.secondary"
                fontWeight={600}
                sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}
              >
                Followers
              </Typography>
              <Typography 
                variant="h5"
                fontWeight={800}
                color={isDarkMode ? '#FFFFFF' : '#111827'}
                sx={{ fontSize: '1.45rem', letterSpacing: '-0.02em' }}
              >
                {formattedFollowers}
              </Typography>
            </Box>
          )}

          <Box>
              <Typography 
                variant="caption" 
                color="text.secondary"
                fontWeight={600}
                sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}
              >
                Avg Views
              </Typography>
              <Typography 
                variant="body1"
                fontWeight={700}
                color={formattedViews === 'N/A' ? 'text.secondary' : (isDarkMode ? '#FFFFFF' : '#111827')}
              >
                {formattedViews}
              </Typography>
            </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
